<template>
    <div id="search-input">
        <base-chips-input
            :value="selectedContacts"
            :suggestions="options"
            :item-label="contactLabel"
            :item-key="contact => contact.phoneNumber"
            :parse="parsePhone"
            :filter="false"
            :loading="loading"
            :create-first="isPhone"
            :disabled="disabled"
            separator=","
            commit-on-blur
            chip-class="is-flash"
            :placeholder="$t('recipient-placeholder')"
            :add-placeholder="$t('add-recipient')"
            :label="$tc('recipient', 1)"
            @input="updateList"
            @search="searchContacts"
        >
            <template #option="{ option }">
                <template v-if="option.create">{{ option.label }}</template>
                <template v-else>
                    {{ option.item.name }}
                    <br v-if="option.item.name">
                    {{ formatPhoneNumber(option.item.phoneNumber) }}
                </template>
            </template>
        </base-chips-input>
    </div>
</template>
<script>

// Internal components
import BaseChipsInput from '@/core/components/BaseChipsInput.vue';

// Import external libraries
import { mapActions, mapState } from 'vuex';
import { EventBus }             from '../../../helpers/EventBus.js';
import { formatPhoneNumber, isValidPhoneNumber, phoneNumberError, toE164 } from '@/libraries/PhoneNumber';

const PHONE_CHARACTERS = new RegExp('^[0-9+\\-.() ]+$');

export default {
    components: { BaseChipsInput },

    props: ['defaultList', 'disabled'],

    data () {
        return {
            query:            '',
            selectedContacts: [],
            loading:          false
        };
    },

    mounted () {
        // Clear the contact list
        EventBus.$off('clear-contact-list');
        EventBus.$on('clear-contact-list', () => {
            this.selectedContacts = [];
        });

        if (this.defaultList.length > 0) {
            this.showDefaultList();
        }
    },

    computed: {
        ...mapState('core/contacts', [
            'contacts'
        ]),

        options () {
            return Object.values(this.contacts).map(element => {
                const hasName = element.attributes['first-name'] !== null && element.attributes['last-name'] !== null;

                return {
                    id:          element.id,
                    name:        hasName ? `${element.attributes['first-name']} ${element.attributes['last-name']}` : '',
                    phoneNumber: element.attributes['phone-number']
                };
            });
        },

        isPhone () {
            return PHONE_CHARACTERS.test(this.query);
        }
    },

    methods: {
        formatPhoneNumber,

        ...mapActions('core/contacts', [
            'index'
        ]),

        /**
         * If the default list props contain contacts show them
         */
        showDefaultList () {
            this.updateList(this.defaultList.map(phone => this.findContact(phone)));
        },

        /**
         * Label of a contact chip
         *
         * @param {object}  contact  the contact
         *
         * @return {string}
         */
        contactLabel (contact) {
            return contact.name || formatPhoneNumber(contact.phoneNumber);
        },

        /**
         * Get a known contact from its phone number, or a new one
         *
         * @param {string}  phone  the phone number in E.164 format
         *
         * @return {object}
         */
        findContact (phone) {
            return this.options.find(element => element.phoneNumber === phone) || {
                id:          null,
                name:        phone,
                phoneNumber: phone
            };
        },

        /**
         * Search a contact
         *
         * @param {string}  query  the typed text
         */
        searchContacts (query) {
            const searchDelay = 500;

            this.query = query;
            clearTimeout(this.timer);

            if (query === '') {
                this.loading = false;
                return;
            }

            this.loading = true;
            this.timer   = setTimeout(() => {
                this.index({
                    filters: {
                        'first-name|last-name|phone-number': query.replace('+', ''),
                        'sms-contact':                       true
                    }
                })
                    .then(() => {
                        this.loading = false;
                    });
            }, searchDelay);
        },

        /**
         * Check a typed phone number before adding it to the list
         *
         * @param {string}  text  the typed text
         *
         * @return {object}
         */
        parsePhone (text) {
            if (!isValidPhoneNumber(text)) {
                return {
                    error: PHONE_CHARACTERS.test(text) ?
                        this.$i18n.t(this.phoneErrorKey(text)) : this.$i18n.t('contact-not-found')
                };
            }

            const phone = toE164(text);

            if (this.selectedContacts.some(contact => contact.phoneNumber === phone)) {
                return { error: this.$i18n.t('phone-number-exists') };
            }

            return { value: this.findContact(phone) };
        },

        /**
         * Translation key describing why a phone number was refused
         *
         * @param {string}  text  the refused phone number
         *
         * @return {string}
         */
        phoneErrorKey (text) {
            const keys = {
                TOO_SHORT:       'phone-too-short',
                TOO_LONG:        'phone-too-long',
                INVALID_LENGTH:  'phone-invalid-length',
                INVALID_COUNTRY: 'phone-invalid-country',
                UNKNOWN_NUMBER:  'phone-unknown-number'
            };

            return keys[phoneNumberError(text)] || 'invalid-phone-number';
        },

        /**
         * Update the selected list
         *
         * @param {array}  contacts  the selected contacts
         */
        updateList (contacts) {
            this.selectedContacts = contacts;
            this.$emit('listUpdated', this.selectedContacts);
        }
    }
};
</script>
